import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const API_BASE = "https://api.kallabakari.is";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// --- Icelandic date formatting (no locale dependency)
const IS_MONTHS = [
  "janúar",
  "febrúar",
  "mars",
  "apríl",
  "maí",
  "júní",
  "júlí",
  "ágúst",
  "september",
  "október",
  "nóvember",
  "desember",
];

const formatDateIS = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getDate()}. ${IS_MONTHS[date.getMonth()]}`;
};

const formatISK = (n) =>
  new Intl.NumberFormat("is-IS", {
    maximumFractionDigits: 0,
  }).format(Number.isFinite(Number(n)) ? Number(n) : 0);

// --- Product helpers
const productTitle = (p) => {
  const d = p?.details || {};

  if (p?.type === "bite") return d.name || "Smáréttir";
  if (p?.type === "minidonut") return "Mini-donuts";
  if (p?.type === "bread") return d.bread || "Brauð";
  if (p?.type === "cake") return d.cake || "Kaka";

  return p?.type || "Vara";
};

const productMetaLines = (p, order) => {
  const d = p?.details || {};
  const lines = [];

  if (p?.type === "bite") {
    if (d.description) lines.push(d.description);
    if (d.people) lines.push(`Fyrir ${d.people} manns`);
    lines.push(`Magn: ${d.quantity ?? 1}`);
    return lines;
  }

  if (p?.type === "cake") {
    if (d.size) lines.push(`Stærð: ${d.size}`);
    if (d.filling) lines.push(`Fylling: ${d.filling}`);
    if (d.bottom) lines.push(`Botn: ${d.bottom}`);
    if (d.smjorkrem) lines.push(`Smjörkrem: ${d.smjorkrem}`);
    if (d.text) lines.push(`Texti: ${d.text}`);
    if (d.skreyting) lines.push(`Skreyting: ${d.skreyting}`);
    if (order?.image) lines.push("Mynd fylgir pöntun");
    lines.push(`Magn: ${d.quantity ?? 1}`);
    return lines;
  }

  // bread/minidonut/other
  lines.push(`Magn: ${d.quantity ?? 1}`);
  return lines;
};

export default function OrderSuccess() {
  const query = useQuery();
  const orderId = query.get("orderId");

  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading"); // loading | paid | pending | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderId) {
      setState("error");
      setError("Vantar orderId.");
      return;
    }

    (async () => {
      try {
        setState("loading");
        setError("");

        const res = await fetch(`${API_BASE}/api/orders/${orderId}`);
        const text = await res.text();

        if (!res.ok) throw new Error(`HTTP ${res.status}: ${text}`);

        const json = JSON.parse(text);
        const o = json?.order ?? json;

        setOrder(o);
        setState(o?.paymentStatus === "paid" ? "paid" : "pending");
      } catch (e) {
        setState("error");
        setError(e?.message || "Villa kom upp.");
      }
    })();
  }, [orderId]);

  const products = order?.products || [];

  const total = useMemo(() => {
    return products.reduce((sum, p) => {
      const d = p?.details || {};
      const qty = Number(d.quantity ?? 1);
      const price = Number(d.price ?? 0);
      if (!Number.isFinite(qty) || !Number.isFinite(price)) return sum;
      return sum + qty * price;
    }, 0);
  }, [products]);

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl bg-white rounded-2xl border border-amber-200 shadow-sm p-8">
        {/* Header */}
        {state === "paid" ? (
          <div className="text-center mb-6">
            <div className="text-4xl mb-3">✓</div>
            <h1 className="text-3xl text-stone-900 font-serif tracking-wide mb-2">Greiðsla tókst!</h1>
            <p className="text-stone-500 font-serif">
              Pöntunin þín hefur verið móttekin og munt þú fá staðfestingu á tölvupósti.
            </p>
          </div>
        ) : null}

        {state === "pending" ? (
          <div className="text-center mb-6">
            <h1 className="text-2xl text-stone-900 font-serif tracking-wide mb-2">Greiðsla í vinnslu…</h1>
            <p className="text-stone-500 font-serif">
              Við erum að bíða eftir staðfestingu. Endurnýjaðu síðuna ef þörf er á.
            </p>
          </div>
        ) : null}

        {state === "error" ? (
          <div className="text-center mb-6">
            <h1 className="text-2xl text-stone-900 font-serif tracking-wide mb-2">Villa</h1>
            <p className="text-red-500 font-serif">{error}</p>
          </div>
        ) : null}

        {/* Order meta */}
        {order ? (
          <div className="space-y-1.5 font-serif text-sm border-b border-amber-100 pb-5 mb-6">
            <div className="text-stone-500">
              <span className="text-stone-700 font-semibold">Pöntunarnúmer:</span>{" "}
              <span className="font-mono text-stone-600 text-xs">{order.id}</span>
            </div>
            <div className="text-stone-600"><span className="text-stone-700 font-semibold">Nafn:</span> {order.name}</div>
            <div className="text-stone-600"><span className="text-stone-700 font-semibold">Email:</span> {order.email}</div>
            <div className="text-stone-600"><span className="text-stone-700 font-semibold">Sími:</span> {order.phone}</div>
            <div className="mt-3 text-stone-700 font-serif">
              Þú getur sótt pöntunina í verslun okkar á{" "}
              <span className="font-semibold">Innnesveg 1</span> þann{" "}
              <span className="font-semibold">{formatDateIS(order.date)}</span>.
            </div>
          </div>
        ) : null}

        {/* Products */}
        <h2 className="text-[#C29979] font-serif text-xs uppercase tracking-wider mb-3">Vörur</h2>

        <div className="space-y-3">
          {products.map((p, i) => {
            const d = p?.details || {};
            const qty = d.quantity ?? 1;
            const price = d.price ?? 0;
            const lineTotal = (Number(qty) || 0) * (Number(price) || 0);
            const meta = productMetaLines(p, order);

            return (
              <div key={i} className="rounded-xl border border-amber-100 bg-amber-50/40 p-4 flex justify-between items-start gap-4">
                <div className="min-w-0">
                  <div className="text-stone-900 font-serif font-medium text-sm">{productTitle(p)}</div>
                  <div className="mt-1 space-y-0.5">
                    {meta.map((m, idx) => (
                      <div key={idx} className="text-stone-400 text-xs font-serif">{m}</div>
                    ))}
                  </div>
                </div>
                <div className="text-stone-800 font-serif text-sm font-semibold shrink-0">
                  {formatISK(lineTotal)} kr
                </div>
              </div>
            );
          })}

          {products.length === 0 ? (
            <div className="text-stone-400 font-serif text-sm">Engar vörur fundust á pöntuninni.</div>
          ) : null}
        </div>

        {/* Total */}
        <div className="mt-5 border-t border-amber-100 pt-4 flex justify-between items-center">
          <span className="text-stone-700 font-serif font-semibold">Samtals</span>
          <span className="text-[#C29979] text-xl font-serif font-semibold">
            {formatISK(order?.totalAmount ?? total)} kr
          </span>
        </div>

        {/* Back button */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-block h-12 px-8 leading-[3rem] rounded-xl bg-[#C29979] text-white font-serif uppercase text-sm hover:bg-[#b08060] transition-colors"
          >
            Til baka á forsíðu
          </Link>
        </div>
      </div>
    </div>
  );
}
